import { ChordCakeState, SectionState, RowState, SlotState } from '../types';
import { ChordObjectModelV3 } from '@chordbaker/music-theory';

/**
 * Validate that an object conforms to ChordCakeState (V4, the in-memory shape) structure
 */
export function isValidChordCakeState(data: any): data is ChordCakeState {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check required field: title
  if (typeof data.title !== 'string') {
    return false;
  }

  // Check required field: sections array
  if (!Array.isArray(data.sections)) {
    return false;
  }

  // Validate each section
  for (const section of data.sections) {
    if (!isValidSection(section)) {
      return false;
    }
  }

  // Optional key validation
  // key can be null or a KeySignature object
  if (data.key !== undefined && data.key !== null) {
    const isKeySignatureObject = typeof data.key === 'object' &&
                                  data.key.note &&
                                  data.key.mode;
    if (!isKeySignatureObject) {
      return false;
    }
  }

  // guitarShapes is required and should be an object (not array, not null)
  if (typeof data.guitarShapes !== 'object' || data.guitarShapes === null || Array.isArray(data.guitarShapes)) {
    return false;
  }

  return true;
}

/**
 * Validate section structure
 */
export function isValidSection(section: any): section is SectionState {
  if (!section || typeof section !== 'object') {
    return false;
  }

  // Check required fields: id, title
  if (typeof section.id !== 'string' || typeof section.title !== 'string') {
    return false;
  }

  // Check required field: rows array
  if (!Array.isArray(section.rows)) {
    return false;
  }

  // Validate each row
  for (const row of section.rows) {
    if (!isValidRow(row)) {
      return false;
    }
  }

  return true;
}

/**
 * Validate row structure
 */
export function isValidRow(row: any): row is RowState {
  if (!row || typeof row !== 'object') {
    return false;
  }

  // Check required field: id
  if (typeof row.id !== 'string') {
    return false;
  }

  // Check required field: bars array (V4: a row holds bars, a bar holds slots)
  if (!Array.isArray(row.bars)) {
    return false;
  }

  // Validate each bar and its slots
  for (const bar of row.bars) {
    if (!bar || typeof bar !== 'object' || typeof bar.id !== 'string' || !Array.isArray(bar.slots) || bar.slots.length === 0) {
      return false;
    }
    for (const slot of bar.slots) {
      if (!isValidSlot(slot)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Validate slot structure
 */
export function isValidSlot(slot: any): slot is SlotState {
  if (!slot || typeof slot !== 'object') {
    return false;
  }

  // Check required field: id
  if (typeof slot.id !== 'string') {
    return false;
  }

  // Chord can be null or an object
  if (slot.chord !== null && typeof slot.chord !== 'object') {
    return false;
  }

  // If chord exists, validate its structure
  if (slot.chord !== null) {
    if (!isValidChordObject(slot.chord)) {
      return false;
    }
  }

  // lyrics: string | null (V3: never undefined)
  if (slot.lyrics !== undefined && slot.lyrics !== null && typeof slot.lyrics !== 'string') {
    return false;
  }

  // customText: string | null (V3: never undefined)
  if (slot.customText !== undefined && slot.customText !== null && typeof slot.customText !== 'string') {
    return false;
  }

  return true;
}

/**
 * Validate chord object structure
 * Supports V3 (root-based) format
 */
function isValidChordObject(chord: any): chord is ChordObjectModelV3 {
  if (!chord || typeof chord !== 'object') {
    return false;
  }

  // Check required field: root (can be null or Note object)
  if (chord.root !== null && chord.root !== undefined) {
    const isNoteObject = typeof chord.root === 'object' &&
                         chord.root.noteName &&
                         chord.root.modifier;
    if (!isNoteObject) {
      return false;
    }
  }

  // Check quality field (can be null)
  if (chord.quality !== null && typeof chord.quality !== 'string') {
    return false;
  }

  // Check extension field (can be null)
  if (chord.extension !== null && typeof chord.extension !== 'string') {
    return false;
  }

  // Check isDominant field (required boolean)
  if (typeof chord.isDominant !== 'boolean') {
    return false;
  }

  // Check adds field (required array)
  if (!Array.isArray(chord.adds)) {
    return false;
  }

  // Validate adds array contains only strings
  for (const add of chord.adds) {
    if (typeof add !== 'string') {
      return false;
    }
  }

  // Check alterations field (required array)
  if (!Array.isArray(chord.alterations)) {
    return false;
  }

  // Validate alterations array contains only strings
  for (const alteration of chord.alterations) {
    if (typeof alteration !== 'string') {
      return false;
    }
  }

  // Check bass field (can be null or Note object)
  if (chord.bass !== null && chord.bass !== undefined) {
    const isNoteObject = typeof chord.bass === 'object' &&
                         chord.bass.noteName &&
                         chord.bass.modifier;
    if (!isNoteObject) {
      return false;
    }
  }

  return true;
}
